import { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { 
  Mail, UserX, UserPlus, Download, Upload, Edit, Trash2, Plus, RefreshCw, CalendarIcon,
  FileSpreadsheet, AlertCircle, CheckCircle2, XCircle, Loader2, FileDown
} from 'lucide-react';

const DAYS_OF_WEEK = [
  { id: 'mon', label: 'Mon' }, { id: 'tue', label: 'Tue' }, { id: 'wed', label: 'Wed' },
  { id: 'thu', label: 'Thu' }, { id: 'fri', label: 'Fri' }, { id: 'sat', label: 'Sat' },
  { id: 'sun', label: 'Sun' }
];

export const InviteDialog = ({ 
  open, onOpenChange, inviteForm, setInviteForm, onInvite, departments, userRole, isFreePlan 
}) => {
  const [emailInput, setEmailInput] = useState('');

  const addEmail = (email) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return;
    }
    if (inviteForm.emails.includes(trimmed)) return;
    setInviteForm({ ...inviteForm, emails: [...inviteForm.emails, trimmed] });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      if (emailInput.trim()) {
        addEmail(emailInput);
        setEmailInput('');
      }
    }
    if (e.key === 'Backspace' && !emailInput && inviteForm.emails.length > 0) {
      setInviteForm({ ...inviteForm, emails: inviteForm.emails.slice(0, -1) });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const emails = pasted.split(/[,;\s\n]+/).filter(Boolean);
    const newEmails = [...inviteForm.emails];
    emails.forEach(email => {
      const trimmed = email.trim().toLowerCase();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) && !newEmails.includes(trimmed)) {
        newEmails.push(trimmed);
      }
    });
    setInviteForm({ ...inviteForm, emails: newEmails });
  };

  const removeEmail = (email) => {
    setInviteForm({ ...inviteForm, emails: inviteForm.emails.filter(e => e !== email) });
  };

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md" data-testid="invite-modal">
      <DialogHeader>
        <DialogTitle>Invite Employees</DialogTitle>
        <DialogDescription>Send invitations to join your workspace. Add multiple emails at once.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>Email Addresses *</Label>
          <div className="min-h-[44px] p-2 border border-slate-200 dark:border-slate-700 rounded-lg flex flex-wrap gap-1.5 items-center cursor-text focus-within:ring-2 focus-within:ring-blue-500 bg-white dark:bg-slate-900"
            onClick={() => document.getElementById('email-tag-input')?.focus()}>
            {inviteForm.emails.map((email) => (
              <span key={email} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm">
                {email}
                <button type="button" onClick={() => removeEmail(email)} className="hover:text-red-500 ml-0.5 font-bold">&times;</button>
              </span>
            ))}
            <input
              id="email-tag-input"
              type="text"
              placeholder={inviteForm.emails.length === 0 ? "Type email and press Enter, or paste multiple" : "Add more..."}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={() => { if (emailInput.trim()) { addEmail(emailInput); setEmailInput(''); } }}
              className="flex-1 min-w-[180px] border-0 outline-none bg-transparent text-sm text-slate-900 dark:text-white placeholder:text-slate-400"
              data-testid="invite-email-input"
            />
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500">Press Enter, comma, or Tab to add. Paste multiple emails separated by commas.</p>
        </div>
        <div className="space-y-2">
          <Label>Role (same for all)</Label>
          <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })} disabled={isFreePlan}>
            <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="employee">Employee</SelectItem>
              {!isFreePlan && userRole === 'admin' && <SelectItem value="manager">Manager</SelectItem>}
              {!isFreePlan && userRole === 'admin' && <SelectItem value="admin">Admin</SelectItem>}
            </SelectContent>
          </Select>
          {userRole === 'manager' && (
            <p className="text-xs text-slate-500 mt-1">As a manager, you can only invite employees.</p>
          )}
        </div>
        <div className="space-y-2">
          <Label>Department (Optional)</Label>
          <Select value={inviteForm.department || 'none'} onValueChange={(v) => setInviteForm({ ...inviteForm, department: v === 'none' ? '' : v })}>
            <SelectTrigger data-testid="invite-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Department</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Personal Message (Optional)</Label>
          <Textarea placeholder="Add a personal note to the invitation..."
            value={inviteForm.personal_message}
            onChange={(e) => setInviteForm({ ...inviteForm, personal_message: e.target.value })}
            rows={2} data-testid="invite-message-input" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onInvite} disabled={inviteForm.emails.length === 0} data-testid="send-invite-btn">
          <Mail className="w-4 h-4 mr-2" />
          {inviteForm.emails.length <= 1 ? 'Send Invite' : `Send ${inviteForm.emails.length} Invites`}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export const BulkInviteDialog = ({ 
  open, onOpenChange, bulkInvites, setBulkInvites, onBulkInvite, onDownloadTemplate, onUpload 
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-2xl" data-testid="bulk-invite-modal">
      <DialogHeader>
        <DialogTitle>Bulk Invite Employees</DialogTitle>
        <DialogDescription>Upload a CSV file to invite multiple employees at once</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onDownloadTemplate} data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-2" />Download Template
          </Button>
          <div className="relative">
            <input type="file" accept=".csv" onChange={onUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="csv-upload-input" />
            <Button variant="outline"><Upload className="w-4 h-4 mr-2" />Upload CSV</Button>
          </div>
        </div>
        {bulkInvites.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-800 px-4 py-2 border-b">
              <p className="text-sm font-medium">{bulkInvites.length} employees to invite</p>
            </div>
            <div className="max-h-60 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-left">Email</th>
                    <th className="px-4 py-2 text-left">Role</th>
                    <th className="px-4 py-2 text-left">Department</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {bulkInvites.map((inv, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">{inv.name || '-'}</td>
                      <td className="px-4 py-2">{inv.email}</td>
                      <td className="px-4 py-2">{inv.role}</td>
                      <td className="px-4 py-2">{inv.department || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => { onOpenChange(false); setBulkInvites([]); }}>Cancel</Button>
        <Button onClick={onBulkInvite} disabled={bulkInvites.length === 0} data-testid="send-bulk-invites-btn">
          <Mail className="w-4 h-4 mr-2" />Send {bulkInvites.length} Invites
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const EditEmployeeDialog = ({
  open, onOpenChange, employee, setEmployee, departments, employees, orgLeaveTypes, userRole, onSave, isFreePlan, onGenerateDeviceToken
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-employee-modal">
      <DialogHeader>
        <DialogTitle>Edit Employee Profile</DialogTitle>
        <DialogDescription>Update employee information and settings</DialogDescription>
      </DialogHeader>
      {employee && (
        <div className="space-y-6 py-4">
          {employee.activity && (
            <Card className="bg-slate-50 dark:bg-slate-800/50">
              <CardContent className="p-4">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Activity Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{employee.activity.weekly_hours}h</p>
                    <p className="text-xs text-slate-500">This Week</p>
                  </div>
                  <div>
                    <Badge variant={employee.activity.last_timesheet_status === 'approved' ? 'default' : 'secondary'}>
                      {employee.activity.last_timesheet_status}
                    </Badge>
                    <p className="text-xs text-slate-500 mt-1">Last Timesheet</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={employee.name || ''} onChange={(e) => setEmployee({ ...employee, name: e.target.value.replace(/[0-9]/g, '') })} data-testid="edit-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={employee.email || ''} onChange={(e) => setEmployee({ ...employee, email: e.target.value.trim() })} data-testid="edit-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Job Title</Label>
              <Input value={employee.job_title || ''} onChange={(e) => setEmployee({ ...employee, job_title: e.target.value })} placeholder="e.g. Software Engineer" data-testid="edit-job-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select value={employee.department || 'none'} onValueChange={(v) => setEmployee({ ...employee, department: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="edit-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Department</SelectItem>
                  {departments.map(dept => (<SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Employee ID</Label>
              <Input value={employee.employee_id_number || ''} onChange={(e) => setEmployee({ ...employee, employee_id_number: e.target.value })} placeholder="e.g. EMP-001" data-testid="edit-employee-id-input" />
            </div>
            <div className="space-y-2">
              <Label>Date of Joining</Label>
              <Input type="date" value={employee.date_of_joining || ''} onChange={(e) => setEmployee({ ...employee, date_of_joining: e.target.value })} data-testid="edit-doj-input" />
            </div>
            {!isFreePlan && (
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={employee.role || 'employee'} onValueChange={(v) => setEmployee({ ...employee, role: v })} disabled={userRole !== 'admin'}>
                <SelectTrigger data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              {userRole === 'manager' && <p className="text-xs text-slate-500">Only admins can change employee roles.</p>}
            </div>
            )}
            {!isFreePlan && (
            <div className="space-y-2">
              <Label>Manager</Label>
              <Select value={employee.manager_id || 'none'} onValueChange={(v) => setEmployee({ ...employee, manager_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="edit-manager-select"><SelectValue placeholder="Select manager" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {employees.filter(e => e.id !== employee.id && ['admin', 'manager'].includes(e.role)).map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select value={employee.timezone || 'UTC'} onValueChange={(v) => setEmployee({ ...employee, timezone: v })}>
                <SelectTrigger data-testid="edit-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">UTC</SelectItem>
                  <SelectItem value="America/New_York">Eastern Time</SelectItem>
                  <SelectItem value="America/Chicago">Central Time</SelectItem>
                  <SelectItem value="America/Denver">Mountain Time</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                  <SelectItem value="Europe/London">London</SelectItem>
                  <SelectItem value="Europe/Paris">Paris</SelectItem>
                  <SelectItem value="Asia/Kolkata">India (IST)</SelectItem>
                  <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                  <SelectItem value="Asia/Singapore">Singapore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Account Status</Label>
              <Select value={employee.account_status || 'active'} onValueChange={(v) => setEmployee({ ...employee, account_status: v })}>
                <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Desktop Tracking Mode */}
            <div className="space-y-2 pt-3 border-t border-slate-100 dark:border-slate-800">
              <Label className="text-sm font-semibold">Desktop Tracking Mode</Label>
              <Select value={employee.tracking_mode || 'normal'} onValueChange={(v) => setEmployee({ ...employee, tracking_mode: v })}>
                <SelectTrigger data-testid="edit-tracking-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal (Employee controls timer)</SelectItem>
                  <SelectItem value="stealth">Stealth (Invisible, auto-tracking)</SelectItem>
                </SelectContent>
              </Select>
              {employee.tracking_mode === 'stealth' && (
                <div className="space-y-2 pl-2 pt-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Screenshot Interval (minutes)</Label>
                    <Select value={String(employee.stealth_screenshot_interval || 10)} onValueChange={(v) => setEmployee({ ...employee, stealth_screenshot_interval: parseInt(v) })}>
                      <SelectTrigger data-testid="edit-screenshot-interval"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">Every 5 minutes</SelectItem>
                        <SelectItem value="10">Every 10 minutes</SelectItem>
                        <SelectItem value="15">Every 15 minutes</SelectItem>
                        <SelectItem value="30">Every 30 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Idle Timeout (minutes)</Label>
                    <Select value={String(employee.stealth_idle_timeout || 15)} onValueChange={(v) => setEmployee({ ...employee, stealth_idle_timeout: parseInt(v) })}>
                      <SelectTrigger data-testid="edit-idle-timeout"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 minutes</SelectItem>
                        <SelectItem value="10">10 minutes</SelectItem>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">App will be invisible. Auto clock-in on boot, screenshots captured silently.</p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <Label>Working Days Override</Label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <label key={day.id} className={`flex items-center justify-center w-12 h-10 rounded-lg border-2 cursor-pointer transition-all ${
                  employee.working_days?.includes(day.id)
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                }`}>
                  <input type="checkbox" className="sr-only"
                    checked={employee.working_days?.includes(day.id) || false}
                    onChange={(e) => {
                      const newDays = e.target.checked
                        ? [...(employee.working_days || []), day.id]
                        : (employee.working_days || []).filter(d => d !== day.id);
                      setEmployee({ ...employee, working_days: newDays });
                    }} />
                  <span className="text-sm font-medium">{day.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Daily Hours Target Override</Label>
              <span className="text-sm font-medium text-blue-600">{employee.daily_hours_target || 8}h</span>
            </div>
            <Slider value={[employee.daily_hours_target || 8]}
              onValueChange={(v) => setEmployee({ ...employee, daily_hours_target: v[0] })}
              min={1} max={16} step={0.5} data-testid="edit-hours-slider" />
          </div>

          {orgLeaveTypes.length > 0 && (
            <div className="space-y-3">
              <Label>Leave Balances</Label>
              <div className={`grid gap-4 ${orgLeaveTypes.length >= 3 ? 'grid-cols-3' : orgLeaveTypes.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {orgLeaveTypes.map((lt) => {
                  const balance = employee.leave_balances?.find(b => b.leave_type === lt.key) || { total_days: 0, used_days: 0 };
                  return (
                    <div key={lt.key} className="space-y-2 p-3 border rounded-lg">
                      <p className="text-sm font-medium">{lt.name}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Total</Label>
                          <Input type="number" min={0} value={balance.total_days}
                            onChange={(e) => {
                              const newBalances = [...(employee.leave_balances || [])];
                              const idx = newBalances.findIndex(b => b.leave_type === lt.key);
                              const nb = { leave_type: lt.key, total_days: parseInt(e.target.value) || 0, used_days: balance.used_days };
                              if (idx >= 0) newBalances[idx] = nb; else newBalances.push(nb);
                              setEmployee({ ...employee, leave_balances: newBalances });
                            }}
                            data-testid={`edit-leave-${lt.key}-total`} />
                        </div>
                        <div>
                          <Label className="text-xs">Used</Label>
                          <Input type="number" min={0} value={balance.used_days}
                            onChange={(e) => {
                              const newBalances = [...(employee.leave_balances || [])];
                              const idx = newBalances.findIndex(b => b.leave_type === lt.key);
                              const nb = { leave_type: lt.key, total_days: balance.total_days, used_days: parseInt(e.target.value) || 0 };
                              if (idx >= 0) newBalances[idx] = nb; else newBalances.push(nb);
                              setEmployee({ ...employee, leave_balances: newBalances });
                            }}
                            data-testid={`edit-leave-${lt.key}-used`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      <DialogFooter className="flex-col sm:flex-row gap-2">
        {onGenerateDeviceToken && employee?.role === 'employee' && (
          <Button variant="outline" onClick={() => onGenerateDeviceToken(employee)} data-testid="generate-device-token-btn" className="mr-auto">
            Generate Device Token
          </Button>
        )}
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSave} data-testid="save-employee-btn">Save Changes</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const DeactivateDialog = ({ open, onOpenChange, employeeName, onDeactivate }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="deactivate-modal">
      <DialogHeader>
        <DialogTitle>Deactivate Employee</DialogTitle>
        <DialogDescription>
          Are you sure you want to deactivate {employeeName}? They will no longer be able to log in, but their data will be preserved.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button variant="destructive" onClick={onDeactivate} data-testid="confirm-deactivate-btn">
          <UserX className="w-4 h-4 mr-2" />Deactivate
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const DeleteEmployeeDialog = ({ open, onOpenChange, employeeName, onDelete }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="delete-employee-modal">
      <DialogHeader>
        <DialogTitle>Delete Employee</DialogTitle>
        <DialogDescription>
          Are you sure you want to permanently delete <strong>{employeeName}</strong>? This action cannot be undone. All their data including time entries, timesheets, and leave records will be removed.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button variant="destructive" onClick={onDelete} data-testid="confirm-delete-employee-btn">
          <Trash2 className="w-4 h-4 mr-2" />Delete Permanently
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const DepartmentDialog = ({
  open, onOpenChange, departments, departmentForm, setDepartmentForm, departmentFormError,
  setDepartmentFormError, editingDepartment, setEditingDepartment, onSave, onDelete, userRole
}) => (
  <Dialog open={open} onOpenChange={(o) => {
    onOpenChange(o);
    if (!o) { setDepartmentForm({ name: '', description: '' }); setDepartmentFormError(''); setEditingDepartment(null); }
  }}>
    <DialogContent className="sm:max-w-lg" data-testid="department-modal">
      <DialogHeader>
        <DialogTitle>{userRole === 'admin' ? 'Manage Departments' : 'View Departments'}</DialogTitle>
        <DialogDescription>
          {userRole === 'admin' ? 'Create and manage departments for your organization' : 'View departments in your organization'}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        {departments.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {departments.map((dept) => (
              <div key={dept.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`department-item-${dept.id}`}>
                <div>
                  <p className="font-medium">{dept.name}</p>
                  <p className="text-sm text-slate-500">{dept.employee_count || 0} employees</p>
                </div>
                {userRole === 'admin' && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => { setEditingDepartment(dept); setDepartmentForm({ name: dept.name, description: dept.description || '' }); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-600" onClick={() => onDelete(dept.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">No departments found</p>
        )}
        {userRole === 'admin' && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium">{editingDepartment ? 'Edit Department' : 'Add New Department'}</h4>
            <div className="space-y-2">
              <Label>Department Name *</Label>
              <Input value={departmentForm.name}
                onChange={(e) => { const val = e.target.value.replace(/[0-9]/g, ''); setDepartmentForm({ ...departmentForm, name: val }); if (departmentFormError) setDepartmentFormError(''); }}
                placeholder="e.g. Engineering" data-testid="department-name-input"
                className={departmentFormError ? 'border-red-500 focus-visible:ring-red-500' : ''} />
              {departmentFormError && <p className="text-xs text-red-500 mt-1">{departmentFormError}</p>}
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea value={departmentForm.description}
                onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })}
                placeholder="Brief description of the department" rows={2} data-testid="department-description-input" />
            </div>
            <div className="flex gap-2">
              {editingDepartment && (
                <Button variant="outline" onClick={() => { setEditingDepartment(null); setDepartmentForm({ name: '', description: '' }); }}>
                  Cancel Edit
                </Button>
              )}
              <Button onClick={onSave} data-testid="save-department-btn">
                <Plus className="w-4 h-4 mr-2" />{editingDepartment ? 'Update' : 'Add'} Department
              </Button>
            </div>
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

export const CreateEmployeeDialog = ({
  open, onOpenChange, form, setForm, departments, onSubmit, loading, onGeneratePassword, isFreePlan, isFullStealthPolicy
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md" data-testid="create-employee-modal">
      <DialogHeader>
        <DialogTitle>Create Employee</DialogTitle>
        <DialogDescription>
          {isFullStealthPolicy
            ? 'Create an employee account for stealth tracking. They will be enrolled via device token — no password or email login needed.'
            : 'Create an employee account directly without email invitation'}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="emp-name">Full Name *</Label>
          <Input id="emp-name" placeholder="John Doe" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value.replace(/[0-9]/g, '') })} data-testid="create-emp-name-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="emp-email">Email Address *</Label>
          <Input id="emp-email" type="email" placeholder="john@inspiroworks.com" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-emp-email-input" />
        </div>
        {!isFreePlan && (
        <div className="space-y-2">
          <Label>Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="create-emp-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        )}
        <div className="space-y-2">
          <Label>Department</Label>
          <Select value={form.department || 'none'} onValueChange={(v) => setForm({ ...form, department: v === 'none' ? '' : v })}>
            <SelectTrigger data-testid="create-emp-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Department</SelectItem>
              {departments.map(dept => (<SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {!isFullStealthPolicy && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="emp-password">Temporary Password *</Label>
            <Button type="button" variant="ghost" size="sm" onClick={onGeneratePassword}>
              <RefreshCw className="w-3 h-3 mr-1" />Generate
            </Button>
          </div>
          <Input id="emp-password" type="text" placeholder="Min 6 characters" value={form.temporary_password}
            onChange={(e) => setForm({ ...form, temporary_password: e.target.value })} data-testid="create-emp-password-input" />
          <p className="text-xs text-slate-500">Share this password with the employee. They can change it after login.</p>
        </div>
        )}
        {isFullStealthPolicy && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
            <strong>Full Stealth Mode:</strong> No password required. After creating the employee, use the <em>Generate Device Token</em> action and install the desktop app on their laptop with that token.
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSubmit} disabled={loading} data-testid="create-emp-submit-btn">
          {loading ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Creating...</>) : (<><UserPlus className="w-4 h-4 mr-2" />Create Employee</>)}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ===========================
// IMPORT EMPLOYEES DIALOG
// ===========================
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ImportEmployeesDialog = ({ open, onOpenChange, token, onImportComplete }) => {
  const [step, setStep] = useState('upload'); // upload -> preview -> result
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const handleClose = () => {
    setStep('upload');
    setPreview(null);
    setImportResult(null);
    setUploading(false);
    setImporting(false);
    onOpenChange(false);
    if (importResult?.created > 0) onImportComplete?.();
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/people/import-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee_import_template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download template error:', err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert('Please upload an Excel file (.xlsx)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/people/import-preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || 'Failed to parse file');
        return;
      }
      setPreview(data);
      setStep('preview');
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const validRows = preview.rows.filter(r => r.is_valid);
      const res = await fetch(`${API_URL}/api/admin/people/import-confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: validRows })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || 'Import failed');
        return;
      }
      setImportResult(data);
      setStep('result');
    } catch (err) {
      console.error('Import error:', err);
      alert('Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="import-employees-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            {step === 'upload' && 'Import Employees from Excel'}
            {step === 'preview' && 'Review Import Data'}
            {step === 'result' && 'Import Complete'}
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Download the template, fill in employee details, then upload it back.'}
            {step === 'preview' && 'Review the parsed data below. Fix any errors in Excel and re-upload, or proceed.'}
            {step === 'result' && 'Here are the results of your import.'}
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1: UPLOAD */}
        {step === 'upload' && (
          <div className="space-y-6 py-4">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5">
              <h3 className="font-semibold text-emerald-800 dark:text-emerald-300 mb-2">Step 1: Download Template</h3>
              <p className="text-sm text-emerald-700 dark:text-emerald-400 mb-3">
                Download the Excel template with pre-configured columns and dropdown validations. Fill in your employee details and save.
              </p>
              <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2" data-testid="download-template-btn">
                <FileDown className="w-4 h-4" /> Download Template (.xlsx)
              </Button>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">Step 2: Upload Filled Template</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                Upload the filled Excel file. Only <strong>First Name, Last Name, and Email</strong> are required. All other fields are optional.
              </p>
              <div className="flex items-center gap-3">
                <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" id="import-file" />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="upload-excel-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Parsing...' : 'Upload Excel File'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: PREVIEW */}
        {step === 'preview' && preview && (
          <div className="space-y-4 py-2">
            {/* Summary badges */}
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="outline" className="text-sm gap-1 px-3 py-1">
                Total: {preview.total_rows}
              </Badge>
              <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-sm gap-1 px-3 py-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Valid: {preview.valid}
              </Badge>
              {preview.invalid > 0 && (
                <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-sm gap-1 px-3 py-1">
                  <XCircle className="w-3.5 h-3.5" /> Errors: {preview.invalid}
                </Badge>
              )}
            </div>

            {preview.seat_warning && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {preview.seat_warning}
              </div>
            )}

            {/* Preview table */}
            <div className="max-h-[400px] overflow-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">#</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Name</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Email</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Role</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Department</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Emp Type</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Pay Rate</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, i) => (
                    <tr key={i} className={`border-t ${!row.is_valid ? 'bg-red-50 dark:bg-red-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>
                      <td className="px-3 py-2 text-slate-400">{row.row_number}</td>
                      <td className="px-3 py-2 font-medium text-slate-800 dark:text-white">{row.name}</td>
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-400">{row.email}</td>
                      <td className="px-3 py-2 capitalize">{row.role}</td>
                      <td className="px-3 py-2">{row.department || '-'}</td>
                      <td className="px-3 py-2 capitalize">{row.employment_type?.replace('_', '-')}</td>
                      <td className="px-3 py-2">{row.pay_rate ? `Rs. ${Number(row.pay_rate).toLocaleString('en-IN')}` : '-'}</td>
                      <td className="px-3 py-2">
                        {row.is_valid ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <CheckCircle2 className="w-3 h-3" /> Ready
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600" title={row.errors.join(', ')}>
                            <XCircle className="w-3 h-3" /> {row.errors[0]}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setStep('upload'); setPreview(null); }}>
                Re-upload
              </Button>
              <Button 
                onClick={handleConfirmImport} 
                disabled={importing || preview.valid === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="confirm-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                {importing ? 'Importing...' : `Import ${preview.valid} Employee${preview.valid !== 1 ? 's' : ''}`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* STEP 3: RESULT */}
        {step === 'result' && importResult && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-sm gap-1 px-3 py-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Created: {importResult.created}
              </Badge>
              {importResult.skipped > 0 && (
                <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-sm gap-1 px-3 py-1">
                  Skipped: {importResult.skipped}
                </Badge>
              )}
              {importResult.failed > 0 && (
                <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-sm gap-1 px-3 py-1">
                  <XCircle className="w-3.5 h-3.5" /> Failed: {importResult.failed}
                </Badge>
              )}
            </div>

            <div className="max-h-[350px] overflow-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-left font-semibold">Email</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                    <th className="px-3 py-2 text-left font-semibold">Temp Password</th>
                  </tr>
                </thead>
                <tbody>
                  {importResult.details.map((d, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-2 font-medium">{d.name}</td>
                      <td className="px-3 py-2 text-slate-500">{d.email}</td>
                      <td className="px-3 py-2">
                        {d.status === 'created' && <span className="text-emerald-600 font-medium">Created</span>}
                        {d.status === 'skipped' && <span className="text-amber-600">{d.reason}</span>}
                        {d.status === 'failed' && <span className="text-red-600">{d.reason}</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-700 dark:text-slate-300">
                        {d.temp_password || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-slate-500">
              Employees created with "Pending Setup" status. Review their profiles, then use "Send Invitation" or "Send All Invitations" to activate their accounts and email login credentials.
            </p>

            <DialogFooter>
              <Button onClick={handleClose} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="import-done-btn">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

